import React from 'react';
import { TrendingUp, Activity, Heart, Zap, BarChart3, List, Clock } from 'lucide-react';
import { ControlOption } from './shared/types';
import { chartThemeDark, seriesColors, palette, ChartTheme } from '@repo/ui/theme';

export const CHART_CONFIG = {
  DEFAULT_HEIGHT: 400,
  COMPACT_HEIGHT: 200,
  MODAL_HEIGHT: 500,
  heights: {
    default: 400,
    compact: 200,
    modal: 500,
  },
  MARGINS: {
    DEFAULT: { top: 20, right: 20, bottom: 60, left: 60 },
    COMPACT: { top: 10, right: 10, bottom: 40, left: 40 },
    MODAL: { top: 20, right: 60, bottom: 80, left: 60 },
    BAR_CHART: { top: 20, right: 20, bottom: 90, left: 60 },
    LINE_CHART: { top: 20, right: 80, bottom: 80, left: 80 },
  },
  MOBILE_MARGINS: {
    DEFAULT: { top: 15, right: 20, bottom: 60, left: 45 },
    COMPACT: { top: 10, right: 10, bottom: 40, left: 35 },
    MODAL: { top: 10, right: 20, bottom: 60, left: 50 },
    BAR_CHART: { top: 15, right: 20, bottom: 100, left: 50 },
    LINE_CHART: { top: 15, right: 20, bottom: 75, left: 45 },
  },
  MOBILE_HEIGHTS: {
    default: 280,
    modal: 300,
    ranking: 260,
    trend: 260,
  },
  COLORS: {
    PRIMARY: seriesColors[1],
    SECONDARY: seriesColors[0],
    SUCCESS: '#10b981',
    WARNING: palette.yellow,
    ERROR: palette.red,
    NEUTRAL: palette.neutral,
  },
  WELLNESS_CHART_COLORS: {
    PRE: seriesColors[0],
    POST: seriesColors[1],
  },
  WELLNESS_TRENDS_COLOR: palette.red,
  PADDING: 0.3,
  TICK_SIZE: 5,
  TICK_PADDING: 5,
  TICK_ROTATION: -45,
  LEGEND_OFFSET: -40,
} as const;

export const DATA_LIMITS = {
  DEFAULT_ITEMS: 10,
  MAX_CHART_ITEMS: 50,
  TITLE_MAX_LENGTH: 20,
  TABLE_TITLE_MAX_LENGTH: 30,
} as const;

export const ANALYTICS_CONFIG = {
  METRIC_LABELS: {
    SESSIONS: 'Sessions',
    MINUTES: 'Minutes',
    UTILIZATION: 'Utilization',
    CHANGE: 'Change',
    PERIOD: 'Period',
    SHARE: 'Share',
  },
  TIME_PERIODS: {
    CURRENT_MONTH: 'This Month',
  },
  VIEW_MODES: {
    CHART: 'chart' as const,
    TABLE: 'table' as const,
  },
  METRIC_TYPES: {
    SESSIONS: 'sessions' as const,
    MINUTES: 'minutes' as const,
  },
} as const;

export const UI_CONFIG = {
  ICON_SIZES: {
    SMALL: 16,
    MEDIUM: 20,
    LARGE: 24,
  },
  SPACING: {
    SM: 8,
    MD: 16,
  },
} as const;

export const METRICS_CONFIG = {
  PERCENTAGE_DECIMAL_PLACES: 1,
  DEFAULT_VALUE: 0,
  DEFAULT_STRING: '—',
  MIN_VALUE_FOR_GROWTH: 0,
  THOUSAND_THRESHOLD: 1000,
} as const;

export const CHART_THEMES: Record<string, ChartTheme> = {
  default: chartThemeDark,
} as const;

export const CHART_THEME = CHART_THEMES.default;

export interface AnalyticsCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  isImplemented: boolean;
}

export const ANALYTICS_CATEGORIES: AnalyticsCategory[] = [
  {
    id: 'session-performance',
    title: 'Session Insights',
    description: 'See how members engage with different MindGym sessions and content over time.',
    route: '/team-insights/sessions',
    icon: TrendingUp,
    isImplemented: true,
  },
  {
    id: 'utilization',
    title: 'Utilization Insights',
    description: 'Track overall MindGym participation patterns and engagement across teams.',
    route: '/team-insights/utilization',
    icon: Activity,
    isImplemented: true,
  },
  {
    id: 'wellness',
    title: 'Wellness Insights',
    description: 'See how MindGym sessions influence stress, mood, and overall wellbeing.',
    route: '/team-insights/wellness',
    icon: Heart,
    isImplemented: true,
  },
  {
    id: 'system-performance',
    title: 'System Insights',
    description: 'Monitor system health and performance metrics',
    route: '/team-insights/system-performance',
    icon: Zap,
    isImplemented: false,
  },
];

export const getCategoryById = (id: string): AnalyticsCategory | undefined => {
  return ANALYTICS_CATEGORIES.find(category => category.id === id);
};

export const getImplementedCategories = (): AnalyticsCategory[] => {
  return ANALYTICS_CATEGORIES.filter(category => category.isImplemented);
};

export const VIEW_OPTIONS: ControlOption<'chart' | 'table'>[] = [
  {
    value: ANALYTICS_CONFIG.VIEW_MODES.CHART,
    label: 'Chart',
    icon: <BarChart3 className="h-4 w-4" />,
  },
  {
    value: ANALYTICS_CONFIG.VIEW_MODES.TABLE,
    label: 'Table',
    icon: <List className="h-4 w-4" />,
  },
];

export const COMMON_VIEW_OPTIONS = VIEW_OPTIONS;

export const COMMON_METRIC_OPTIONS: ControlOption<'sessions' | 'minutes'>[] = [
  {
    value: 'sessions',
    label: ANALYTICS_CONFIG.METRIC_LABELS.SESSIONS,
    icon: <Activity className="h-4 w-4" />,
  },
  {
    value: 'minutes',
    label: ANALYTICS_CONFIG.METRIC_LABELS.MINUTES,
    icon: <Clock className="h-4 w-4" />,
  },
];

export const METRIC_OPTIONS = COMMON_METRIC_OPTIONS;

export const TIME_CONFIG = {
  MINUTES_PER_HOUR: 60,
  MS_PER_MINUTE: 60 * 1000,
  MINUTES_THRESHOLD_FOR_HOURS: 60,
} as const;

export const CHART_DATA_CONFIG = {
  LINE_COLORS: {
    SESSIONS: seriesColors[0],
    MINUTES: seriesColors[1],
  },
  BAR_COLORS: [seriesColors[0]],
  DECIMAL_PLACES: 1,
} as const;

export const CHART_COLORS = [...seriesColors];

export const CHART_MARGINS = {
  default: CHART_CONFIG.MARGINS.DEFAULT,
  compact: CHART_CONFIG.MARGINS.COMPACT,
  bar: { ...CHART_CONFIG.MARGINS.BAR_CHART, bottom: 100 },
};
